import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { alumniPhotoUrl } from '../../utils/assets';

// Array of different colors for avatars without photos
const avatarColors = [
  'bg-red-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-yellow-600',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-lime-600',
  'bg-emerald-500',
  'bg-rose-500',
  'bg-violet-500',
  'bg-fuchsia-500'
];

const AlumniShowcase = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(12);

  useEffect(() => {
    fetchAlumni();
  }, []);

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const response = await api.get('/alumni/directory?limit=50&page=1');
      if (response.data.success) {
        const alumniWithData = response.data.alumni.filter(a => a.profile_image || a.full_name);
        setAlumni(alumniWithData);
      }
    } catch (error) {
      console.error('Error fetching alumni for showcase:', error);
      setAlumni([]);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = alumniPhotoUrl;

  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const secondInitial = nameParts[1]?.charAt(0).toUpperCase() || '';
    return `${firstInitial}${secondInitial}`;
  };

  const getAvatarColor = (name) => {
    if (!name) return avatarColors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    const index = Math.abs(hash) % avatarColors.length;
    return avatarColors[index];
  };

  const displayedAlumni = alumni.slice(0, displayCount);

  if (loading) {
    return (
      <div className="py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  if (alumni.length === 0) {
    return null;
  }

  return (
    <div className="py-4">
      {/* Centered Alumni Grid - Transparent background with staggered animation */}
      <div className="flex flex-wrap justify-center gap-3 md:gap-4">
        {displayedAlumni.map((alumnus, index) => (
          <div
            key={alumnus.id}
            className="flex flex-col items-center group animate-scale-in"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            {/* Circular Avatar */}
            <div className="relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full overflow-hidden shadow-md transition-all duration-300 hover:scale-110 hover:shadow-xl ring-2 ring-white/30">
              {/* Initials sit underneath so a missing or broken photo degrades gracefully */}
              <div
                className={`absolute inset-0 ${getAvatarColor(alumnus.full_name)} flex items-center justify-center`}
                aria-hidden="true"
              >
                <span className="text-white text-xs sm:text-sm font-bold">
                  {getInitials(alumnus.full_name)}
                </span>
              </div>
              {getImageUrl(alumnus.profile_image) ? (
                <img
                  src={getImageUrl(alumnus.profile_image)}
                  alt={alumnus.full_name || 'Alumni member'}
                  className="relative w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
            </div>
            {/* Name on hover */}
            <div className="mt-1 opacity-0 group-hover:opacity-100 transition-all duration-300">
              <p className="text-xs text-white/90 text-center truncate max-w-[80px] font-medium">
                {alumnus.full_name?.split(' ')[0]}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button with animation */}
      {alumni.length > displayCount && (
        <div className="text-center mt-6">
          <button
            onClick={() => setDisplayCount(displayCount + 12)}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/80 backdrop-blur-sm text-white rounded-lg hover:bg-primary transition-all duration-300 hover:scale-105 text-xs hover:shadow-lg"
          >
            Load More Alumni
            <svg className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      )}

      {/* Animation Keyframes */}
      <style>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-scale-in {
          animation: scale-in 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default AlumniShowcase;
